// ── Azure Bot Service automated provisioning ──────────────────────────────────
//
// Thin API client for provisioning Azure Bot resources on behalf of a user via
// OAuth2 delegated access. Used by the Teams channel setup wizard to eliminate
// the manual "go to Azure portal and create a bot" step.
//
// Two Azure planes are involved:
//   1. Microsoft Graph (graph.microsoft.com) — create Entra app registrations
//      and client secrets. Requires Application.ReadWrite.All Delegated
//      permission (the narrower .OwnedBy scope is Application-only and
//      cannot be used in a delegated OAuth flow — see the scope const
//      comment below for the security rationale).
//   2. Azure Resource Manager (management.azure.com) — create the Bot Service
//      resource that registers the bot with the Bot Framework. Requires
//      user_impersonation delegation on the ARM scope.
//
// References:
//   https://learn.microsoft.com/en-us/graph/api/application-post-applications
//   https://learn.microsoft.com/en-us/rest/api/resources/subscriptions/list
//   https://learn.microsoft.com/en-us/rest/api/botservice/bot-service/create

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const ARM_BASE = 'https://management.azure.com';
const AAD_AUTHORIZE_BASE = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const AAD_TOKEN_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/token';
const BOT_SERVICE_API_VERSION = '2022-09-15';
const ARM_SUBSCRIPTIONS_API_VERSION = '2022-12-01';
const ARM_RESOURCE_GROUPS_API_VERSION = '2021-04-01';

// Delegated scopes for the two-plane provisioning flow.
//
// NOTE on Graph scope: Application.ReadWrite.OwnedBy exists only as an
// Application permission (client_credentials), not a Delegated one. The
// delegated flow we use here requires Application.ReadWrite.All — the only
// delegated permission that authorises creating app registrations. The
// elevated scope is gated by the user's tenant role (Application
// Administrator or higher must consent), which is the security control we
// rely on instead of scope narrowing.
// Scopes for the two Azure resources this flow touches. The v2.0 *token*
// endpoint issues a token for exactly one resource per request — combining
// resources in a single token request fails with AADSTS28000. So each resource
// gets its own scope set, redeemed in separate token requests (see
// exchangeAzureCodeForTokens). offline_access/openid/profile ride along with the
// first (ARM) request so we get a refresh_token to mint the Graph token from.
const AAD_OIDC_SCOPES = ['offline_access', 'openid', 'profile'];

// ARM (Delegated): list subscriptions + resource groups + create Bot Service.
export const AZURE_ARM_SCOPES = [
  ...AAD_OIDC_SCOPES,
  'https://management.azure.com/user_impersonation',
];

// Graph (Delegated): create Entra app registrations + publish the Teams app to
// the org catalog. ENG-5984: AppCatalog.ReadWrite.All is admin-gated (delegated-
// only) — bundling it here means the provisioning consent also covers auto-
// publishing the Teams app. Included in AZURE_GRAPH_SCOPES so it lands in BOTH
// the consent screen (AZURE_PROVISIONING_SCOPES) and the Graph token request.
//
// ENG-6002: split base vs optional. Tenants whose consent grant predates the
// AppCatalog scope SSO past the consent screen (prompt=select_account) and then
// fail the full-scope token mint with AADSTS65001 — provisioning must fall back
// to the base scope it actually needs rather than failing outright.
/** The Graph scope provisioning itself cannot work without. */
export const AZURE_GRAPH_BASE_SCOPES = [
  'https://graph.microsoft.com/Application.ReadWrite.All',
];
/** Admin-gated convenience scopes (org app-catalog publish — ENG-5984). */
export const AZURE_GRAPH_OPTIONAL_SCOPES = [
  'https://graph.microsoft.com/AppCatalog.ReadWrite.All',
];
export const AZURE_GRAPH_SCOPES = [
  ...AZURE_GRAPH_BASE_SCOPES,
  ...AZURE_GRAPH_OPTIONAL_SCOPES,
];

// Combined scope list — used ONLY for the authorize/consent screen, which
// accepts multiple resources so the user consents to both in one prompt. Never
// pass this to a token request: the token endpoint rejects multi-resource scope
// (AADSTS28000).
export const AZURE_PROVISIONING_SCOPES = [
  ...AAD_OIDC_SCOPES,
  ...AZURE_GRAPH_SCOPES,
  'https://management.azure.com/user_impersonation',
];

// ── Errors ───────────────────────────────────────────────────────────────────

export class AzureProvisioningError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'AzureProvisioningError';
  }
}

// ── OAuth2 helpers ────────────────────────────────────────────────────────────

export interface AzureOAuthConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
}

/** Build a Microsoft OAuth2 authorization URL for the popup flow. */
export function buildAzureAuthUrl(
  config: AzureOAuthConfig,
  state: string,
): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    redirect_uri: config.redirectUri,
    response_mode: 'query',
    scope: AZURE_PROVISIONING_SCOPES.join(' '),
    state,
    prompt: 'select_account',
  });
  return `${AAD_AUTHORIZE_BASE}?${params.toString()}`;
}

export interface AzureTokenSet {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: string; // ISO-8601
  id_token?: string;
  tenant_id: string;
}

/**
 * POST the AAD token endpoint and parse the response into an AzureTokenSet.
 * Shared by the authorization-code and refresh-token grants — the only
 * difference is the grant-specific parameters and the requested scopes (which
 * must target a single resource, plus the OIDC scopes).
 */
async function requestAzureToken(
  config: AzureOAuthConfig,
  grantParams: Record<string, string>,
  scopes: string[],
): Promise<AzureTokenSet> {
  const body = new URLSearchParams({
    client_id: config.clientId,
    client_secret: config.clientSecret,
    scope: scopes.join(' '),
    ...grantParams,
  });

  const res = await fetch(AAD_TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  const data = await res.json() as Record<string, unknown>;
  if (!res.ok || data['error']) {
    throw new AzureProvisioningError(
      `Token exchange failed: ${String(data['error_description'] ?? data['error'] ?? res.statusText)}`,
      res.status,
      data,
    );
  }

  const expiresIn = typeof data['expires_in'] === 'number' ? data['expires_in'] : 3600;
  const expiresAt = new Date(Date.now() + expiresIn * 1000).toISOString();

  // Extract tenant from the id_token (iss claim) or token endpoint response.
  let tenantId = 'common';
  const idToken = typeof data['id_token'] === 'string' ? data['id_token'] : null;
  if (idToken) {
    try {
      const payload = JSON.parse(
        Buffer.from(idToken.split('.')[1] ?? '', 'base64url').toString(),
      ) as { tid?: string };
      if (payload.tid) tenantId = payload.tid;
    } catch { /* best-effort */ }
  }

  // Validate required tokens are present — silent empty strings make the
  // downstream ARM/Graph calls fail with cryptic 401s. Surface the failure
  // here with the original AAD response payload attached for diagnosis.
  const accessToken = data['access_token'];
  if (typeof accessToken !== 'string' || accessToken.length === 0) {
    throw new AzureProvisioningError(
      'Token response missing access_token',
      res.status,
      data,
    );
  }
  const refreshToken = data['refresh_token'];

  return {
    access_token: accessToken,
    // refresh_token is optional when offline_access wasn't granted — keep it
    // as an empty string rather than throwing so the immediate provisioning
    // flow still works (refresh is a follow-up convenience).
    refresh_token: typeof refreshToken === 'string' ? refreshToken : '',
    expires_in: expiresIn,
    expires_at: expiresAt,
    id_token: idToken ?? undefined,
    tenant_id: tenantId,
  };
}

/**
 * Exchange an authorization code for an ARM-scoped token (+ refresh token).
 *
 * Scoped to ARM only — the v2.0 token endpoint rejects multi-resource scope
 * (AADSTS28000). offline_access rides along so we get a refresh_token, which
 * {@link refreshAzureToken} then redeems for a Graph token.
 */
export async function exchangeAzureCode(
  code: string,
  config: AzureOAuthConfig,
  scopes: string[] = AZURE_ARM_SCOPES,
): Promise<AzureTokenSet> {
  return requestAzureToken(
    config,
    { code, grant_type: 'authorization_code', redirect_uri: config.redirectUri },
    scopes,
  );
}

/**
 * Redeem a refresh token for an access token scoped to a different resource.
 *
 * The refresh_token minted alongside the ARM token (consent was granted for
 * both resources at the authorize step) can be redeemed for a Graph token — the
 * standard cross-resource pattern that sidesteps the single-resource-per-token
 * limit.
 */
export async function refreshAzureToken(
  refreshToken: string,
  config: AzureOAuthConfig,
  scopes: string[],
): Promise<AzureTokenSet> {
  return requestAzureToken(
    config,
    { refresh_token: refreshToken, grant_type: 'refresh_token' },
    scopes,
  );
}

/** Per-resource access tokens for the two-plane provisioning flow. */
export interface AzureProvisioningTokens {
  /** ARM token — list subscriptions/resource groups, create the Bot Service. */
  armAccessToken: string;
  /** Graph token — create the Entra app registration + client secret. */
  graphAccessToken: string;
  /** Tenant ID from the authorizing user's id_token. */
  tenantId: string;
  /** ARM token expiry (ISO-8601); the Graph token expires around the same time. */
  expiresAt: string;
  /**
   * ENG-6002: true when the Graph token had to be minted WITHOUT the optional
   * AppCatalog.ReadWrite.All scope because the tenant's consent grant predates
   * ENG-5984 (full-scope mint → AADSTS65001). Provisioning works normally; the
   * org-catalog publish step is unavailable until an admin re-consents.
   */
  catalogConsentMissing?: boolean;
}

/**
 * Exchange an authorization code for BOTH the ARM and Graph access tokens the
 * provisioning flow needs.
 *
 *   1. Redeem the code for an ARM token (+ refresh_token via offline_access).
 *   2. Redeem the refresh_token for a Graph token.
 *
 * The refresh_token never leaves the server — only the two short-lived access
 * tokens are returned to the caller.
 */
export async function exchangeAzureCodeForTokens(
  code: string,
  config: AzureOAuthConfig,
): Promise<AzureProvisioningTokens> {
  const arm = await exchangeAzureCode(code, config, AZURE_ARM_SCOPES);
  if (!arm.refresh_token) {
    throw new AzureProvisioningError(
      'No refresh_token returned from the code exchange — cannot obtain a Microsoft Graph token. ' +
        'Ensure the offline_access scope is granted.',
    );
  }
  let graph: AzureTokenSet;
  let catalogConsentMissing = false;
  try {
    graph = await refreshAzureToken(arm.refresh_token, config, AZURE_GRAPH_SCOPES);
  } catch (err) {
    // ENG-6002: a consent grant that predates the AppCatalog scope (ENG-5984)
    // fails the full-scope mint with AADSTS65001 — the authorize popup SSOs
    // past the consent screen (prompt=select_account), so the new scope never
    // got granted. Provisioning only needs the base scope: retry with it and
    // flag the missing catalog consent so the publish UX degrades gracefully
    // instead of the whole exchange failing.
    if (
      err instanceof AzureProvisioningError &&
      err.message.includes('AADSTS65001')
    ) {
      graph = await refreshAzureToken(arm.refresh_token, config, AZURE_GRAPH_BASE_SCOPES);
      catalogConsentMissing = true;
    } else {
      throw err;
    }
  }

  return {
    armAccessToken: arm.access_token,
    graphAccessToken: graph.access_token,
    tenantId: arm.tenant_id,
    expiresAt: arm.expires_at,
    catalogConsentMissing,
  };
}

// ── ARM helpers ───────────────────────────────────────────────────────────────

export interface AzureSubscription {
  subscriptionId: string;
  displayName: string;
  state: string;
}

/** List Azure subscriptions the delegated user can access. */
export async function listAzureSubscriptions(
  accessToken: string,
): Promise<AzureSubscription[]> {
  const url = `${ARM_BASE}/subscriptions?api-version=${ARM_SUBSCRIPTIONS_API_VERSION}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new AzureProvisioningError(`List subscriptions failed (${res.status}): ${body}`, res.status);
  }
  const data = await res.json() as { value?: unknown[] };
  const subs = (data.value ?? []) as Array<{
    subscriptionId?: string;
    displayName?: string;
    state?: string;
  }>;
  return subs
    .filter((s) => s.subscriptionId && s.displayName)
    .map((s) => ({
      subscriptionId: s.subscriptionId!,
      displayName: s.displayName!,
      state: s.state ?? 'Unknown',
    }));
}

export interface AzureResourceGroup {
  name: string;
  location: string;
}

/** List resource groups within a subscription. */
export async function listAzureResourceGroups(
  accessToken: string,
  subscriptionId: string,
): Promise<AzureResourceGroup[]> {
  const url =
    `${ARM_BASE}/subscriptions/${encodeURIComponent(subscriptionId)}/resourcegroups` +
    `?api-version=${ARM_RESOURCE_GROUPS_API_VERSION}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new AzureProvisioningError(
      `List resource groups failed (${res.status}): ${body}`,
      res.status,
    );
  }
  const data = await res.json() as { value?: unknown[] };
  const groups = (data.value ?? []) as Array<{ name?: string; location?: string }>;
  return groups
    .filter((g) => g.name)
    .map((g) => ({ name: g.name!, location: g.location ?? 'unknown' }));
}

// ── Graph helpers (app registration) ─────────────────────────────────────────

interface GraphApplication {
  id: string;        // internal object id
  appId: string;     // application (client) id — what callers know as "app_id"
  displayName: string;
}

async function graphPost<T>(
  path: string,
  accessToken: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${GRAPH_BASE}${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await res.json() as Record<string, unknown>;
  if (!res.ok) {
    const msg = String(
      (data['error'] as Record<string, unknown> | undefined)?.['message'] ?? res.statusText,
    );
    throw new AzureProvisioningError(`Graph ${path} failed (${res.status}): ${msg}`, res.status, data);
  }
  return data as T;
}

/** Create an Entra multi-tenant app registration for the agent bot. */
async function createAppRegistration(
  accessToken: string,
  displayName: string,
): Promise<GraphApplication> {
  return graphPost<GraphApplication>('/applications', accessToken, {
    displayName,
    // Single-tenant: Azure Bot Service deprecated multitenant bot creation
    // (InvalidBotCreationData), and the runtime already authenticates against
    // the bot's home tenant (see msteams-api.ts), so the bot lives in the
    // authorizing user's tenant.
    signInAudience: 'AzureADMyOrg',
    requiredResourceAccess: [],
  });
}

/** Extract the Graph `error.code` string from an AAD error payload, if present. */
function graphErrorCode(detail: unknown): string | undefined {
  const err = (detail as Record<string, unknown> | undefined)?.['error'];
  const code = (err as Record<string, unknown> | undefined)?.['code'];
  return typeof code === 'string' ? code : undefined;
}

/**
 * Create the service principal (enterprise application) for the app registration.
 *
 * A single-tenant app registration (`signInAudience: 'AzureADMyOrg'`) does NOT
 * get a service principal provisioned automatically — multi-tenant apps got one
 * for free on first consent, but single-tenant apps must be registered in the
 * tenant explicitly via `POST /servicePrincipals { appId }`. Without the service
 * principal the tenant has the app but nothing to authenticate against, and
 * token acquisition fails at runtime with AADSTS7000229 ("missing service
 * principal in the tenant").
 *
 * Idempotent: if a service principal for this appId already exists (re-runs, or
 * one Azure created on our behalf) Graph returns 409
 * `Request_MultipleObjectsWithSameKeyValue`, which we treat as success.
 */
async function createServicePrincipal(
  accessToken: string,
  appId: string,
): Promise<void> {
  try {
    await graphPost<{ id: string; appId: string }>('/servicePrincipals', accessToken, { appId });
  } catch (err) {
    if (
      err instanceof AzureProvisioningError &&
      err.status === 409 &&
      graphErrorCode(err.detail) === 'Request_MultipleObjectsWithSameKeyValue'
    ) {
      // Service principal already exists — nothing to do. Narrow on purpose:
      // only the duplicate-key 409 is idempotent. Any other 409 conflict is a
      // real failure and must propagate, rather than letting provisioning
      // continue without a valid service principal.
      return;
    }
    throw err;
  }
}

interface PasswordCredential {
  secretText: string;
  keyId: string;
  endDateTime: string;
}

/** Add a client secret to an existing app registration. */
async function addClientSecret(
  accessToken: string,
  objectId: string,
  displayName: string,
): Promise<PasswordCredential> {
  return graphPost<PasswordCredential>(
    `/applications/${encodeURIComponent(objectId)}/addPassword`,
    accessToken,
    {
      passwordCredential: {
        displayName,
        // 2-year lifetime — matches Azure portal defaults for bots.
        endDateTime: new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000).toISOString(),
      },
    },
  );
}

// ── ARM Bot Service creation ───────────────────────────────────────────────────

interface BotServiceResource {
  id: string;
  name: string;
  properties: {
    msaAppId: string;
    endpoint: string;
    msaAppObjectId?: string;
  };
}

/**
 * Create (PUT) an Azure Bot Service resource.
 *
 * The resource registers the bot with the Bot Framework and configures the
 * messaging endpoint. The ARM write requires `Microsoft.BotService/botServices/write`
 * which is included in the Contributor role.
 */
async function createBotServiceResource(
  accessToken: string,
  opts: {
    subscriptionId: string;
    resourceGroup: string;
    botName: string;
    displayName: string;
    appId: string;
    appObjectId: string;
    webhookUrl: string;
    tenantId: string;
  },
): Promise<BotServiceResource> {
  const url =
    `${ARM_BASE}/subscriptions/${encodeURIComponent(opts.subscriptionId)}` +
    `/resourceGroups/${encodeURIComponent(opts.resourceGroup)}` +
    `/providers/Microsoft.BotService/botServices/${encodeURIComponent(opts.botName)}` +
    `?api-version=${BOT_SERVICE_API_VERSION}`;

  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      kind: 'sdk',
      location: 'global',
      sku: { name: 'F0' },
      properties: {
        displayName: opts.displayName,
        msaAppId: opts.appId,
        msaAppObjectId: opts.appObjectId,
        // SingleTenant: Azure deprecated MultiTenant bot creation
        // (InvalidBotCreationData). msaAppTenantId is required for SingleTenant
        // and pins the bot to the app's home tenant.
        msaAppType: 'SingleTenant',
        msaAppTenantId: opts.tenantId,
        endpoint: opts.webhookUrl,
        isStreamingSupported: false,
      },
    }),
  });

  if (!res.ok) {
    const body = await res.text();
    throw new AzureProvisioningError(
      `Create Bot Service failed (${res.status}): ${body}`,
      res.status,
    );
  }
  return res.json() as Promise<BotServiceResource>;
}

// ── Teams channel enablement (ENG-5983) ───────────────────────────────────────

const TEAMS_CHANNEL_MAX_ATTEMPTS = 3;
const TEAMS_CHANNEL_BASE_DELAY_MS = 1000;
// Retry the eventual-consistency window (the child PUT can briefly 404/409 the
// just-created parent bot) plus ARM throttling / transient server errors.
const TEAMS_CHANNEL_RETRY_STATUSES = new Set([404, 409, 429, 500, 502, 503, 504]);

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Enable the Microsoft Teams channel on an existing Azure Bot Service resource.
 *
 * `createBotServiceResource` registers the bot but leaves it silent in Teams
 * until the MsTeamsChannel child resource is created — historically a manual
 * Azure-portal step. This PUTs that child resource so provisioning is
 * end-to-end.
 *
 * Idempotent: the channel name is the resource key, so a re-PUT converges and
 * any 2xx (201 on first create, 200 on re-enable) is success. A bounded retry
 * absorbs the eventual-consistency window after the parent bot is created
 * (immediate child writes can transiently 404/409) and ARM throttling (429/5xx).
 *
 * Uses the same `Microsoft.BotService/botServices/.../write` RBAC as the bot
 * resource, so the existing ARM token works — no new scope.
 *
 * Throws `AzureProvisioningError` on terminal failure. Callers that have already
 * created the bot + credentials should treat this as best-effort and not let a
 * failure discard those artifacts.
 */
export async function enableTeamsChannel(
  accessToken: string,
  opts: {
    subscriptionId: string;
    resourceGroup: string;
    botName: string;
    /** Override for tests; defaults to TEAMS_CHANNEL_MAX_ATTEMPTS. */
    maxAttempts?: number;
    /** Override for tests; defaults to TEAMS_CHANNEL_BASE_DELAY_MS. */
    baseDelayMs?: number;
  },
): Promise<void> {
  const url =
    `${ARM_BASE}/subscriptions/${encodeURIComponent(opts.subscriptionId)}` +
    `/resourceGroups/${encodeURIComponent(opts.resourceGroup)}` +
    `/providers/Microsoft.BotService/botServices/${encodeURIComponent(opts.botName)}` +
    `/channels/MsTeamsChannel?api-version=${BOT_SERVICE_API_VERSION}`;

  // The ARM `botServices/channels` schema marks `kind` required — mirror the
  // parent bot's kind explicitly rather than relying on inheritance (a missing
  // `kind` 400s in some regions and silently passes in others).
  const body = JSON.stringify({
    kind: 'azurebot',
    location: 'global',
    properties: {
      channelName: 'MsTeamsChannel',
      properties: { isEnabled: true },
    },
  });

  const maxAttempts = opts.maxAttempts ?? TEAMS_CHANNEL_MAX_ATTEMPTS;
  const baseDelayMs = opts.baseDelayMs ?? TEAMS_CHANNEL_BASE_DELAY_MS;

  let lastStatus: number | undefined;
  let lastBody = '';
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const res = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body,
    });

    // Any 2xx is success (201 fresh create / 200 idempotent re-enable).
    if (res.ok) return;

    lastStatus = res.status;
    lastBody = await res.text();

    if (attempt < maxAttempts && TEAMS_CHANNEL_RETRY_STATUSES.has(res.status)) {
      await sleep(baseDelayMs * attempt);
      continue;
    }
    break;
  }

  throw new AzureProvisioningError(
    `Enable Teams channel failed (${lastStatus}): ${lastBody}`,
    lastStatus,
    lastBody,
  );
}

// ── Top-level provisioning entrypoint ─────────────────────────────────────────

export interface AzureProvisionBotOptions {
  /** Delegated access token with Graph + ARM scopes. */
  graphAccessToken: string;
  /** Delegated access token specifically for ARM (may differ from Graph token). */
  armAccessToken: string;
  /** Tenant ID extracted from the user's id_token. */
  tenantId: string;
  /** Azure subscription to create the Bot Service resource in. */
  subscriptionId: string;
  /** Resource group to create the Bot Service resource in. */
  resourceGroup: string;
  /** Human-readable display name for both the Entra app and the bot. */
  displayName: string;
  /** The Augmented webhook URL for this agent (the Bot Framework messaging endpoint). */
  webhookUrl: string;
}

export interface AzureProvisionBotResult {
  /** Entra Application (client) ID — maps to `app_id` in MsTeamsChannelConfig. */
  appId: string;
  /** Client secret value (plain text, never persisted here). */
  clientSecret: string;
  /** Tenant ID from the authorizing user's token. */
  tenantId: string;
  /** Bot object ID (Entra directory object ID of the app). Maps to `bot_object_id`. */
  botObjectId: string;
  /** ARM resource ID of the created Bot Service resource. */
  botServiceResourceId: string;
  /**
   * ENG-5983: whether the Microsoft Teams channel was enabled on the bot.
   * Best-effort — `false` means the bot + credentials were provisioned fine but
   * the Teams channel PUT failed and must be retried / enabled manually. Never
   * blocks provisioning (a transient channel failure must not discard the
   * one-time client secret).
   */
  teamsChannelEnabled: boolean;
}

/**
 * Provision an Azure bot end-to-end:
 *  1. Create Entra app registration (Graph)
 *  2. Add a 2-year client secret (Graph)
 *  3. Create the Azure Bot Service resource (ARM) — registers with Bot Framework
 *     and sets the messaging endpoint
 *
 * Returns credentials ready to save into MsTeamsChannelConfig. The caller is
 * responsible for encrypting and persisting them.
 */
export async function provisionAzureBot(
  opts: AzureProvisionBotOptions,
): Promise<AzureProvisionBotResult> {
  // Sanitise the bot name — ARM resource names must match [a-zA-Z0-9-_.~] and
  // be ≤ 42 chars for Bot Service. Derive from displayName, falling back to
  // a timestamped name if sanitisation strips the whole string (emoji-only
  // display names, all-whitespace, etc.) — otherwise the ARM PUT would 400
  // on an empty resource segment with a cryptic message.
  const sanitised = opts.displayName
    .replace(/[^a-zA-Z0-9\-_.~]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 42);
  const botName = sanitised.length > 0 ? sanitised : `augmented-bot-${Date.now()}`;

  const app = await createAppRegistration(opts.graphAccessToken, opts.displayName);

  // Single-tenant app registrations don't get a service principal automatically.
  // Create it before the Bot Service resource so the bot can acquire tokens at
  // runtime (otherwise: AADSTS7000229 "missing service principal in the tenant").
  await createServicePrincipal(opts.graphAccessToken, app.appId);

  const secret = await addClientSecret(
    opts.graphAccessToken,
    app.id,
    'Augmented Team bot secret',
  );

  const botService = await createBotServiceResource(opts.armAccessToken, {
    subscriptionId: opts.subscriptionId,
    resourceGroup: opts.resourceGroup,
    botName,
    displayName: opts.displayName,
    appId: app.appId,
    appObjectId: app.id,
    webhookUrl: opts.webhookUrl,
    tenantId: opts.tenantId,
  });

  // ENG-5983: enable the Teams channel so the bot isn't silent in Teams.
  // Best-effort: the app, secret, and bot resource already exist and the secret
  // is a one-time value Graph won't re-emit — a transient channel-enable failure
  // must not unwind the stack and lose them. The caller surfaces the flag so the
  // channel can be retried / enabled manually.
  let teamsChannelEnabled = false;
  try {
    await enableTeamsChannel(opts.armAccessToken, {
      subscriptionId: opts.subscriptionId,
      resourceGroup: opts.resourceGroup,
      botName,
    });
    teamsChannelEnabled = true;
  } catch {
    teamsChannelEnabled = false;
  }

  return {
    appId: app.appId,
    clientSecret: secret.secretText,
    tenantId: opts.tenantId,
    botObjectId: botService.properties.msaAppObjectId ?? app.id,
    botServiceResourceId: botService.id,
    teamsChannelEnabled,
  };
}
